<template>
  <div class="compare-jobs-panel px-6 py-6">
    <div class="compare-jobs-section">
      <p class="section-label text-body-2">
        Draft Profiles
      </p>
      <CompareJobsProfileSearch
        :selected-profiles="draftProfiles"
        placeholder="Add Draft Profiles"
        :async-search="searchDraftProfiles"
        :linked-profile-ulids="linkedProfileUlids"
        :linked-profile-tooltip="linkedProfileTooltip"
        @update:selected-profiles="draftProfiles = $event"
      />
    </div>

    <div class="compare-jobs-section">
      <div class="section-header">
        <p class="section-label text-body-2">
          {{ hrisProfilesSectionLabel }}
        </p>
        <button
          v-if="showJobFamily"
          type="button"
          class="job-family-toggle text-body-2"
          @click="handleToggleJobFamily"
        >
          {{ showJobFamilyList ? 'Hide Job Family' : 'Show Job Family' }}
        </button>
      </div>
      <CompareJobsProfileSearch
        :selected-profiles="hrisProfiles"
        :placeholder="addHrisProfilesPlaceholder"
        :async-search="searchHrisProfiles"
        :linked-profile-ulids="linkedProfileUlids"
        :linked-profile-tooltip="linkedProfileTooltip"
        @update:selected-profiles="hrisProfiles = $event"
      />
    </div>

    <CompareJobsJobFamily
      v-if="showJobFamily && showJobFamilyList"
      class="compare-jobs-job-family-slot"
      :family-options="familyOptions"
      :selected-family-key="selectedFamilyKey"
      :selected-ulids="hrisSelectedUlids"
      :linked-profile-ulids="linkedProfileUlids"
      :linked-profile-tooltip="linkedProfileTooltip"
      @update:selected-family-key="selectedFamilyKey = $event"
      @toggle-profile="handleToggleHrisProfile"
    />

    <v-btn
      color="brandPrimary"
      variant="flat"
      rounded="lg"
      class="compare-jobs-cta text-button-3"
      :disabled="!canCompare"
      @click="handleCompare"
    >
      Compare Jobs
    </v-btn>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { getJobProfileDrafts } from '@/api/jobProfile';
import { getArchitectureTree } from '@/api/architecture';
import CompareJobsProfileSearch from '@/components/jobProfiles/editor/compareJobs/CompareJobsProfileSearch.vue';
import CompareJobsJobFamily from '@/components/jobProfiles/editor/compareJobs/CompareJobsJobFamily.vue';
import {
  canShowJobFamily,
  getDefaultCompareSelection,
  mergeLinkedHrisIntoSelection,
  buildCompareLaunchQuery,
  findFamilyForProfileUlid,
  listArchitectureFamilies,
  filterHrisProfilesFromTree,
  mapFamilyProfilesForList,
  getEditorLinkedProfileUlids,
  getEditorLinkedProfileTooltip,
} from '@/helpers/compareJobsSidebar';
import { useAppAuthData } from '@/stores/app/authData';

const router = useRouter();
const { hrisDisplayName } = storeToRefs(useAppAuthData());
const hrisProfilesSectionLabel = computed(() => `${hrisDisplayName.value} Profiles`);
const addHrisProfilesPlaceholder = computed(() => `Add ${hrisDisplayName.value} Profiles`);
const { addErrorSnackbar } = useAppSnackbar();

const {
  jobProfile, isHrisProfile, isTemplate,
} = storeToRefs(useCurrentJobProfile());

const jobArchitectureStore = useJobArchitecture();
const { treeData } = storeToRefs(jobArchitectureStore);

const draftProfiles = ref([]);
const hrisProfiles = ref([]);
const showJobFamilyList = ref(false);
const selectedFamilyKey = ref('');
const isTreeLoading = ref(false);

const showJobFamily = computed(() => canShowJobFamily({
  isHrisProfile: isHrisProfile.value,
  hrisReferenceUlid: jobProfile.value?.hris_reference_ulid,
}));

const linkedProfileUlids = computed(() => getEditorLinkedProfileUlids({
  isHrisProfile: isHrisProfile.value,
  jobProfile: jobProfile.value,
}));

const linkedProfileTooltip = computed(() => getEditorLinkedProfileTooltip({
  isHrisProfile: isHrisProfile.value,
}));

const hrisSelectedUlids = computed(() => hrisProfiles.value.map((profile) => profile.ulid));

const canCompare = computed(() => (
  draftProfiles.value.length + hrisProfiles.value.length >= 2
));

const familyOptions = computed(() => (
  listArchitectureFamilies(treeData.value).map((entry) => ({
    ...entry,
    profiles: mapFamilyProfilesForList(entry.profiles),
  }))
));

const profileUlidForFamily = computed(() => {
  if (isHrisProfile.value) return jobProfile.value?.ulid;

  return jobProfile.value?.hris_reference_ulid;
});

const applyDefaultSelection = () => {
  const defaults = getDefaultCompareSelection({
    isHrisProfile: isHrisProfile.value,
    jobProfile: jobProfile.value,
  });

  draftProfiles.value = defaults.draftProfiles;
  hrisProfiles.value = defaults.hrisProfiles;
};

const ensureTreeLoaded = async () => {
  if (treeData.value || isTreeLoading.value) return treeData.value;

  isTreeLoading.value = true;

  try {
    treeData.value = await getArchitectureTree();

    return treeData.value;
  } catch (err) {
    addErrorSnackbar({
      text: `Job Architecture load error: ${err.message || err}`,
    });

    throw err;
  } finally {
    isTreeLoading.value = false;
  }
};

const setDefaultFamily = () => {
  const match = findFamilyForProfileUlid(treeData.value, profileUlidForFamily.value);

  if (match) {
    selectedFamilyKey.value = match.key;

    return;
  }

  selectedFamilyKey.value = familyOptions.value[0]?.key || '';
};

const searchDraftProfiles = async (searchText) => {
  const trimmed = (searchText || '').trim();

  if (!trimmed) return [];

  const { documents = [] } = await getJobProfileDrafts({
    mode: 'draft',
    params: {
      sort: 'title',
      page: 1,
      size: 100,
    },
    searchTerm: trimmed,
  });

  return documents
    .filter((profile) => profile.ulid)
    .map((profile) => {
      const title = profile.title || profile.ulid;

      return {
        ulid: profile.ulid,
        title,
        // Checkboxes show option.label, so keep it in sync with the title.
        label: title,
      };
    });
};

const searchHrisProfiles = async (searchText) => {
  const trimmed = (searchText || '').trim();

  if (!trimmed) return [];

  await ensureTreeLoaded();

  return filterHrisProfilesFromTree(treeData.value, trimmed);
};

const handleToggleJobFamily = async () => {
  if (showJobFamilyList.value) {
    showJobFamilyList.value = false;

    return;
  }

  try {
    await ensureTreeLoaded();

    setDefaultFamily();

    showJobFamilyList.value = true;
  } catch {
    showJobFamilyList.value = false;
  }
};

const handleToggleHrisProfile = (profile) => {
  const exists = hrisProfiles.value.some((item) => item.ulid === profile.ulid);

  if (exists) {
    hrisProfiles.value = hrisProfiles.value.filter((item) => item.ulid !== profile.ulid);

    return;
  }

  hrisProfiles.value = [
    ...hrisProfiles.value,
    {
      ulid: profile.ulid,
      title: profile.title || profile.ulid,
    },
  ];
};

const handleCompare = () => {
  if (!canCompare.value || !jobProfile.value?.ulid) return;

  const from = isHrisProfile.value ? 'job-profile-hris-view' : 'job-profile-view';

  router.push({
    name: 'job-architecture-compare',
    query: buildCompareLaunchQuery({
      draftProfiles: draftProfiles.value,
      hrisProfiles: hrisProfiles.value,
      from,
      fromId: jobProfile.value.ulid,
    }),
  });
};

watch(
  () => [jobProfile.value?.ulid, isHrisProfile.value],
  () => {
    if (isTemplate.value) return;

    applyDefaultSelection();

    showJobFamilyList.value = false;
    selectedFamilyKey.value = '';
  },
  { immediate: true },
);

// When a draft links to HRIS while Compare Jobs is open, add that HRIS chip.
watch(
  () => jobProfile.value?.hris_reference_ulid,
  () => {
    if (isTemplate.value || isHrisProfile.value) return;

    hrisProfiles.value = mergeLinkedHrisIntoSelection({
      hrisProfiles: hrisProfiles.value,
      jobProfile: jobProfile.value,
    });
  },
);

onMounted(() => {
  if (!isTemplate.value) {
    applyDefaultSelection();
  }
});
</script>

<style scoped lang="less">
.compare-jobs-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.compare-jobs-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.compare-jobs-job-family-slot {
  flex: 0 1 auto;
  min-height: 0;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.section-label {
  margin: 0;
  color: @text-darkest;
  font-weight: 500;
}

.job-family-toggle {
  border: 0;
  padding: 0;
  background: transparent;
  color: @text-brand;
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
}

.compare-jobs-cta {
  height: 32px !important;
  min-height: 32px !important;
  text-transform: none;
  flex-shrink: 0;
}
</style>
